/**
 * V2 待办操作恢复流。
 *
 * <p>该模块统一处理问题和确认两类挂起边界的恢复：</p>
 * <ul>
 *   <li>都复用最后一条助手气泡作为宿主（若不存在则创建孤儿气泡）</li>
 *   <li>都通过 buildStreamBridge + dispatchEvent 派发 SSE 事件</li>
 *   <li>都有相同的 try / catch / finally 模式（donePayload 处理 + 异常分支 + 收尾）</li>
 * </ul>
 *
 * <p>两类操作都调用同一个 V2 resume SSE 协议。</p>
 */
import {
  aiChatApi,
  postAiV2ResumeStream,
  type AiChatResponse
} from '@/api/aiChat'
import { fetchSseV2Stream } from '@/api/aiChatV2'
import { HttpError } from '@/api/request'
import Toast from '@/components/Toast'
import { useAiModelPreferenceStore } from '@/stores/aiModelPreference'
import { useAiSessionsStore } from '@/stores/aiSessions'
import { pushAiDebug } from '@/utils/aiDebugLog'
import { coalesceServerMessagesForUi } from './messages'
import type { AiChatContext, CancellableAbort } from './context'
import {
  type AiUiMessage,
  type ChatStreamAccumulator,
  genMessageId
} from './types'
import {
  applyMissingDone,
  applyStreamError,
  bindViewSessionIfCurrent,
  buildStreamBridge,
  createStreamAccumulator,
  finalizeAssistantTurn,
  isViewingSession,
  settleStream,
  withOwnerMessages,
  type BridgeDeps,
  type SettleDeps
} from './streamRuntime'
import { createCoalescedDispatcher } from './streamCoalescer'
import { dispatchChatStreamEvent } from './streamEvents'

/** V2 待办恢复所需的最小依赖（来自 AiChatContext）。 */
export interface PendingResumeDeps {
  sessionId: AiChatContext['sessionId']
  messages: AiChatContext['messages']
  lastError: AiChatContext['lastError']
  resumableStop: AiChatContext['resumableStop']
  streamsBySession: AiChatContext['streamsBySession']
  messagesBySession: AiChatContext['messagesBySession']
  aiPlanStore: AiChatContext['aiPlanStore']
  userActionQueue: AiChatContext['userActionQueue']
}

/** 后端"卡已决议/已到期"业务拒绝的文案特征（CardExitReceipts 冲突助手）。 */
const STALE_PENDING_MESSAGE = /超时失效|已到时限|已经处理完毕|已被取消或拒绝|已处理，无需/

/** 对已决议/已到期卡的续跑请求：后端预检 409 拒绝，或预检后竞态以流错误收尾。 */
function isStalePendingActionRejection(err: unknown, acc: ChatStreamAccumulator): boolean {
  if (err instanceof HttpError && err.status === 409) return true
  const message = err instanceof Error ? err.message : String(err)
  return acc.streamErrored && STALE_PENDING_MESSAGE.test(message)
}

/**
 * 死卡点击的自愈（分层补位护栏，2026-09-23）：摘卡 + 静默重拉消息与待办快照 +
 * 轻提示。不渲染错误气泡——这是页面状态过期，不是任务故障；后端扫描器将在
 * 数秒内完成结算与自动收尾，history_updated 事件会让收尾回复自然浮现。
 */
async function reconcileStalePendingAction(
  deps: PendingResumeDeps,
  pendingId: string,
  serverMessage: string
): Promise<void> {
  deps.userActionQueue.value = deps.userActionQueue.value.filter((x) => x.id !== pendingId)
  const sid = deps.sessionId.value?.trim() ?? ''
  if (!sid) return
  if (deps.sessionId.value?.trim() === sid && !deps.streamsBySession.has(sid)) {
    try {
      const res = await aiChatApi.listMessages(sid)
      if (deps.sessionId.value?.trim() === sid && !deps.streamsBySession.has(sid)) {
        const rows = res.data ?? []
        deps.messages.value = coalesceServerMessagesForUi(rows)
        deps.messagesBySession.set(sid, [...deps.messages.value])
      }
    } catch {
      // 静默失败：刷新时的权威快照兜底路径不受影响。
    }
  }
  Toast.info(serverMessage)
}

/**
 * 续写同一轮助手回复的统一流程：复用最后一条助手气泡作为 pending 宿主，
 * 调用 V2 resume API，处理 donePayload、异常分支与收尾。
 *
 * @param deps 上下文依赖
 * @param pendingId 确认 ID 或提问 ID（由具体 API 决定语义）
 * @param callApi V2 resume SSE 调用函数
 * @param debugLabel 用于日志区分的标签
 */
async function runPendingResumeStream(
  deps: PendingResumeDeps,
  pendingId: string,
  callApi: (
    pendingId: string,
    options: {
      signal: AbortSignal
      model: string | null
      onEvent: (eventName: string, data: unknown) => void
    }
  ) => Promise<void>,
  debugLabel: string
): Promise<void> {
  // 守门：仅拦截本会话的并行恢复；其他会话的任务不受影响
  const sid = deps.sessionId.value?.trim() ?? ''
  if (!sid || deps.streamsBySession.has(sid)) {
    pushAiDebug('store', `🔴 ${debugLabel} 早 return(该会话已有在途流)`)
    return
  }
  const sessionsStore = useAiSessionsStore()
  const hadPersistedSession = !sessionsStore.isDraft
  deps.lastError.value = null
  sessionsStore.markRunning(sid)
  pushAiDebug('store', `${debugLabel} 开始(已登记在途流)`)

  // 续写同一轮助手回复：复用最后一条助手气泡，避免再叠一层空时间线上的"正在连接模型…"。
  let resumeMsgId: string
  let resumeTemplate: AiUiMessage
  let hostIdx = -1
  for (let i = deps.messages.value.length - 1; i >= 0; i--) {
    if (deps.messages.value[i].role === 'assistant') {
      hostIdx = i
      break
    }
  }

  if (hostIdx >= 0) {
    const host = deps.messages.value[hostIdx]
    resumeMsgId = host.id
    resumeTemplate = { ...host }
    deps.messages.value.splice(hostIdx, 1, { ...host, pending: true, awaitingNext: false })
  } else {
    const orphan: AiUiMessage = {
      id: genMessageId(),
      role: 'assistant',
      content: '',
      pending: true,
      timeline: [],
      createdAt: Date.now()
    }
    deps.messages.value = [...deps.messages.value, orphan]
    resumeMsgId = orphan.id
    resumeTemplate = orphan
  }

  const streamAcc: ChatStreamAccumulator = createStreamAccumulator()
  streamAcc.ownerSessionId = sid
  streamAcc.registeredSessionId = sid
  const abortCtrl: CancellableAbort = new AbortController()
  deps.streamsBySession.register(sid, abortCtrl)

  const bridgeDeps: BridgeDeps = {
    messages: deps.messages,
    sessionId: deps.sessionId,
    userActionQueue: deps.userActionQueue,
    lastError: deps.lastError,
    resumableStop: deps.resumableStop,
    streamsBySession: deps.streamsBySession,
    aiPlanStore: deps.aiPlanStore,
    messagesBySession: deps.messagesBySession
  }
  const settleDeps: SettleDeps = {
    streamsBySession: deps.streamsBySession,
    userActionQueue: deps.userActionQueue
  }

  const dispatchSseEvent = (eventName: string, data: unknown): void => {
    const bridge = buildStreamBridge(bridgeDeps, {
      pendingMsgId: resumeMsgId,
      pendingMsgTemplate: resumeTemplate,
      sessionsStore,
      hadPersistedSession,
      abortCtrl,
      acc: streamAcc,
      clientUserMsgId: null
    })
    dispatchChatStreamEvent(eventName, data, bridge)
  }
  // 性能债修复(热点1-4降频)：高频流式事件合帧应用，交互/终态事件立即冲刷。
  const dispatcher = createCoalescedDispatcher(dispatchSseEvent)

  /** Recovers a v2 pending-action continuation from its durable SSE journal. */
  const recoverV2Journal = async (): Promise<void> => {
    const runId = streamAcc.v2RunId
    if (!runId || abortCtrl.signal.aborted) return
    let recoveryError: Error | null = null
    await fetchSseV2Stream(runId, {
      fromSeq: (streamAcc.v2LastSeq ?? 0) + 1,
      signal: abortCtrl.signal,
      onEvent: dispatcher.onEvent,
      onError: (error) => {
        recoveryError = error
      }
    })
    dispatcher.flush()
    if (recoveryError) throw recoveryError
  }

  try {
    const modelPref = useAiModelPreferenceStore()
    const modelId = modelPref.selectedApiId?.trim()

    await callApi(pendingId, {
      signal: abortCtrl.signal,
      model: modelId || null,
      onEvent: dispatcher.onEvent
    })
    // 流结束后同步冲刷残余缓冲，再检查终态（幂等）。
    dispatcher.flush()

    if (!streamAcc.doneProcessed && !streamAcc.streamErrored && streamAcc.v2RunId) {
      await recoverV2Journal()
      dispatcher.flush()
    }

    if (streamAcc.doneProcessed) {
      pushAiDebug('store', `${debugLabel}: doneProcessed, 提前 return`)
      return
    }
    if (streamAcc.streamErrored) {
      pushAiDebug('store', `${debugLabel}: streamErrored`)
      sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || deps.sessionId.value)
      return
    }
    const data = streamAcc.donePayload as AiChatResponse | null
    if (data === null) {
      pushAiDebug('store', `${debugLabel}: 未收到 done payload`)
      withOwnerMessages(
        deps.messages,
        deps.messagesBySession,
        deps.sessionId.value,
        streamAcc.ownerSessionId,
        (target) => applyMissingDone(target, deps.lastError, resumeMsgId, isViewingSession(deps.sessionId, streamAcc.ownerSessionId))
      )
      sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || deps.sessionId.value)
      return
    }

    if (data.sessionId) {
      streamAcc.ownerSessionId = data.sessionId
      deps.streamsBySession.move(streamAcc.registeredSessionId ?? sid, data.sessionId)
      streamAcc.registeredSessionId = data.sessionId
      bindViewSessionIfCurrent(deps.sessionId, data.sessionId)
      sessionsStore.registerServerSession(data.sessionId, data.conversationTitle ?? null, {
        activate: isViewingSession(deps.sessionId, data.sessionId)
      })
    }
    if (!hadPersistedSession) {
      void sessionsStore.loadFromServer()
    }
    withOwnerMessages(
      deps.messages,
      deps.messagesBySession,
      deps.sessionId.value,
      streamAcc.ownerSessionId,
      (target) => finalizeAssistantTurn({ messages: target }, resumeMsgId, data)
    )
  } catch (err: unknown) {
    const errMsg = err instanceof Error ? err.message : String(err)
    // 分层补位护栏（2026-09-23）：对已决议/已到期卡的续跑请求被后端业务拒绝——
    // 这是页面状态过期，不是任务故障：摘卡+静默重拉+轻提示，绝不渲染错误气泡。
    if (isStalePendingActionRejection(err, streamAcc)) {
      pushAiDebug('store', `${debugLabel}: stale pending action, reconcile`, { error: errMsg })
      await reconcileStalePendingAction(deps, pendingId, errMsg)
      return
    }
    // 已收到 done 后的连接关闭、用户主动中断导致的 abort（如 BodyStreamBuffer was aborted）
    // 是预期内的正常收尾，不是故障，降级为普通日志，避免控制台误导性红标。
    const expectedAbort = /abort/i.test(errMsg)
      && (streamAcc.doneProcessed || abortCtrl._userCancelled === true)
    if (expectedAbort) {
      pushAiDebug('store', `${debugLabel}: 连接已关闭（done/用户中断，无害）`, { error: errMsg })
    } else {
      pushAiDebug('store', `🔴 ${debugLabel} 流异常`, { error: errMsg })
    }
    if (!streamAcc.doneProcessed && !streamAcc.streamErrored && streamAcc.v2RunId && !abortCtrl._userCancelled) {
      try {
        await recoverV2Journal()
        dispatcher.flush()
      } catch (recoveryError) {
        pushAiDebug('store', `${debugLabel}: v2 journal recovery failed`, {
          error: recoveryError instanceof Error ? recoveryError.message : recoveryError
        })
      }
    }
    if (streamAcc.doneProcessed || streamAcc.streamErrored) {
      return
    }
    if (!streamAcc.doneProcessed) {
      const userCancelled = abortCtrl._userCancelled === true
      const owner = streamAcc.ownerSessionId || deps.sessionId.value
      if (userCancelled) {
        withOwnerMessages(
          deps.messages,
          deps.messagesBySession,
          deps.sessionId.value,
          owner,
          (target) => {
            const idx = target.value.findIndex((m) => m.id === resumeMsgId)
            if (idx >= 0) {
              const prev = target.value[idx]
              target.value.splice(idx, 1, { ...prev, pending: false, stoppedByUser: true })
            }
          }
        )
      } else {
        sessionsStore.abandonSessionTitleGenerating(owner)
        withOwnerMessages(
          deps.messages,
          deps.messagesBySession,
          deps.sessionId.value,
          owner,
          (target) => applyStreamError(
            target,
            deps.lastError,
            resumeMsgId,
            resumeTemplate,
            err,
            isViewingSession(deps.sessionId, owner)
          )
        )
      }
    }
  } finally {
    settleStream(settleDeps, streamAcc.ownerSessionId, sessionsStore)
    pushAiDebug('store', `${debugLabel} finally 触发, 在途流已收尾`)
  }
}

/** Continues a v2 AgentLoop from its persisted pending action. */
export async function streamV2PendingResume(
  deps: PendingResumeDeps,
  pendingId: string,
  actionType: 'QUESTION' | 'CONFIRMATION',
  userChoice: string,
  extraPayload?: Record<string, unknown>
): Promise<void> {
  return runPendingResumeStream(
    deps,
    pendingId,
    (_id, opts) => postAiV2ResumeStream({
      pendingId,
      actionType,
      userChoice,
      extraPayload
    }, opts),
    'v2-pending-resume'
  )
}
